import { useEffect, useState } from "react";
import { Button } from "@/components/button";
import { LinkedAccounts } from "@/components/linked-accounts";
import { CardForm } from "@/app/(app)/settings/_components/card-form";
import { SectionHeader } from "@/app/(app)/settings/_components/section-header";
import { SectionPage } from "@/app/(app)/settings/_components/section-page";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { profileSchema, type ProfileValues } from "@/app/(app)/settings/_schemas/profile";
import * as accountService from "@/src/services/account_service";
import { requestPasswordReset } from "@/src/services/auth_service";
import { useToast } from "@/components/action-feedback";

// Email is read-only (no change-email — backend decision). Password changes
// reuse the reset-password flow: we email a secure link (no in-app current/new
// password form).

export default function AccountManagementPage() {
  // --- profile (name fields owned by the <Form>; email is read-only) ---
  const [savedFirst, setSavedFirst] = useState("");
  const [savedLast, setSavedLast] = useState("");
  // Bumped on each successful save to remount the profile <Form> so its baseline
  // (defaultValues) resets and the dirty-gated Save button disables again.
  const [profileFormKey, setProfileFormKey] = useState(0);
  const [email, setEmail] = useState("");

  // --- async lifecycle state ---
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Password reset reuses the forgot-password flow — emails a secure link.
  const [pwState, setPwState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [pwError, setPwError] = useState<string | null>(null);
  const showToast = useToast();

  async function loadAll() {
    setLoading(true);
    setLoadError(null);
    const profileResult = await accountService.getMe();
    if (!profileResult.ok) {
      setLoadError(profileResult.error.message);
      setLoading(false);
      return;
    }
    setSavedFirst(profileResult.value.firstName);
    setSavedLast(profileResult.value.lastName);
    setEmail(profileResult.value.email);
    setLoading(false);
  }

  useEffect(() => {
    void loadAll();
  }, []);

  async function handleSaveProfile(values: ProfileValues) {
    setSaveError(null);
    const result = await accountService.updateProfile(values);
    if (!result.ok) {
      setSaveError(result.error.message);
      return;
    }
    setSavedFirst(values.firstName);
    setSavedLast(values.lastName);
    setProfileFormKey((k) => k + 1); // remount → baseline resets, Save re-disables
    showToast({ tone: "success", text: "Your information has been successfully updated." });
  }

  async function handleChangePassword() {
    setPwState("sending");
    setPwError(null);
    const result = await requestPasswordReset(email);
    if (!result.ok) {
      setPwError(result.error.message);
      setPwState("error");
      return;
    }
    setPwState("sent");
  }

  // -------------------- loading state --------------------
  if (loading) {
    return (
      <SectionPage title="Account" width="full">
        <SkeletonCard heightClass="h-[140px]" />
        <SkeletonCard heightClass="h-[110px]" />
      </SectionPage>
    );
  }

  // -------------------- load error state --------------------
  if (loadError) {
    return (
      <SectionPage title="Account" width="full">
        <div className="card flex flex-col gap-3">
          <p className="text-h4 text-black">Couldn&apos;t load your account.</p>
          <p className="text-small text-grey-900">{loadError}</p>
          <div>
            <Button hierarchy="primary" size="sm" onClick={() => void loadAll()}>
              Retry
            </Button>
          </div>
        </div>
      </SectionPage>
    );
  }

  // -------------------- populated state --------------------
  return (
    <SectionPage title="Account" width="full">
      {saveError && (
        <div
          role="alert"
          className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
        >
          {saveError}
        </div>
      )}

      <Form
        key={profileFormKey}
        schema={profileSchema}
        defaultValues={{ firstName: savedFirst, lastName: savedLast }}
        onSubmit={handleSaveProfile}
      >
        <CardForm
          title="Profile"
          description="Your name appears on the account and in any audit history."
          action={
            <FormSubmit hierarchy="primary" requireDirty pendingLabel="Saving…">
              Save changes
            </FormSubmit>
          }
        >
          <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
            <FormField name="firstName" label="First name" />
            <FormField name="lastName" label="Last name" />
          </div>
        </CardForm>
      </Form>

      <div className="card @container flex flex-col gap-4">
        <SectionHeader
          title="Email"
          description="Used to log in and to receive your one-time login code."
        />
        <div className="flex items-center rounded-[4px] bg-grey-50 px-3 py-2">
          <span className="truncate text-small text-black">{email}</span>
        </div>
      </div>

      <div className="card @container flex flex-col gap-4">
        <SectionHeader
          title="Password"
          description="We'll email you a secure link to set a new password."
        />
        {pwState === "sent" ? (
          <p
            role="status"
            className="rounded-[4px] border border-green-400 bg-green-100 px-3 py-2 text-small text-green-700"
          >
            Check your inbox — we sent a password reset link to {email}.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {pwError && (
              <p
                role="alert"
                className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
              >
                {pwError}
              </p>
            )}
            <div>
              <Button
                hierarchy="secondary"
                size="sm"
                onClick={() => void handleChangePassword()}
                disabled={pwState === "sending"}
              >
                {pwState === "sending" ? "Sending…" : "Change password"}
              </Button>
            </div>
          </div>
        )}
      </div>
      <LinkedAccounts fill />
    </SectionPage>
  );
}

function SkeletonCard({ heightClass }: { heightClass: string }) {
  return (
    <div className={`card flex animate-pulse flex-col gap-3 ${heightClass}`} aria-hidden>
      <div className="h-5 w-1/3 rounded bg-grey-100" />
      <div className="h-3 w-2/3 rounded bg-grey-100" />
      <div className="flex-1" />
    </div>
  );
}
